import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  Typography,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import {
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
  Close,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { useGetRatingsQuery, useUpdateRatingsMutation } from "../../services/ratings.query";
import { DefaultPage } from "../../layouts/default-page";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { RowActions } from "../common/RowActions";
import { ExportLoadingBackdrop } from "../common/ExportLoadingBackdrop";
import { useTableState } from "../../hooks/useTableState";
import { Notify } from "../../layouts/notify";

const validationSchema = Yup.object({
  description: Yup.string().required("Description is required"),
  main_description: Yup.string().required("Main Description is required"),
});

export default function Ratings() {
  const defaultFilters = useMemo(
    () => ({
      search: "",
    }),
    []
  );

  const {
    page,
    pageSize,
    filters,
    hasActiveFilters,
    setPage,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
  });

  const [draftSearch, setDraftSearch] = useState(filters.search);

  useEffect(() => {
    setDraftSearch(filters.search);
  }, [filters]);

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [ratingFormModalState, setRatingFormModalState] = useState<boolean>(false);
  const [ratingSubmit, { isLoading: ratingSubmitLoading }] = useUpdateRatingsMutation();

  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const {
    data: ratings = [],
    isLoading,
    isFetching,
    isError,
  } = useGetRatingsQuery("");

  const handleOpenRatingFormDialogue = (rowData: any) => {
    setSelectedItem(rowData);
    formik.setValues({
      description: rowData.description || "",
      theme: rowData.theme || "",
      language: rowData.language || "",
      nudity: rowData.nudity || "",
      violence: rowData.violence || "",
      horror: rowData.horror || "",
      drugs: rowData.drugs || "",
      sex: rowData.sex || "",
      main_description: rowData.main_description || "",
    });
    setRatingFormModalState(true);
  };

  const handleCloseRatingFormDialogue = () => {
    setRatingFormModalState(false);
    setSelectedItem(null);
  };

  const formik = useFormik({
    initialValues: {
      description: "",
      theme: "",
      language: "",
      nudity: "",
      violence: "",
      horror: "",
      drugs: "",
      sex: "",
      main_description: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      try {
        await ratingSubmit({
          ...values,
          id: selectedItem.id,
        }).unwrap();
        Notify("info", "Rating updated successfully");
        setRatingFormModalState(false);
        setSelectedItem(null);
      } catch (error: any) {
        console.error("Error during form submission:", error);
        Notify("error", error?.message || "Failed to update Rating");
      }
    },
  });

  const handleSearch = () => {
    setFilters({
      search: draftSearch.trim(),
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const filteredData = useMemo(() => {
    if (!filters.search) return ratings;
    const query = filters.search.toLowerCase();
    return ratings.filter((pr: any) => {
      const searchContent = `${pr?.name || ""} ${pr?.type || ""} ${pr?.main_description || ""}`.toLowerCase();
      return searchContent.includes(query);
    });
  }, [filters.search, ratings]);

  const totalCount = filteredData.length;
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const handleExportPDF = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Preparing PDF document for ${filteredData.length} movie ratings...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const doc = new jsPDF("portrait", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Official Movie Ratings & Classification Standards", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Total Ratings: ${filteredData.length}`,
        14,
        22
      );

      const tableColumn = ["#", "Rating", "Classification Type", "Main Guidance Description"];
      const tableRows = filteredData.map((item: any, idx: number) => [
        idx + 1,
        item.name || "—",
        (item.type || "—").toUpperCase(),
        item.main_description || item.description || "—",
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 9,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8.5,
          cellPadding: 3,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Movie_Ratings_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${filteredData.length} ratings to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Generating Excel spreadsheet for ${filteredData.length} ratings...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const rows = filteredData.map((item: any, idx: number) => ({
        "No.": idx + 1,
        "Rating Code": item.name || "—",
        "Type": (item.type || "—").toUpperCase(),
        "Main Description": item.main_description || "—",
        "Theme": item.theme || "—",
        "Language": item.language || "—",
        "Violence": item.violence || "—",
        "Nudity": item.nudity || "—",
        "Sex": item.sex || "—",
        "Horror": item.horror || "—",
        "Drugs": item.drugs || "—",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Movie Ratings");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 14 },
        { wch: 14 },
        { wch: 45 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
      ];

      XLSX.writeFile(wb, `MTRCB_Movie_Ratings_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${filteredData.length} ratings to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "number",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Typography>{(page - 1) * pageSize + row.index + 1}</Typography>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 140,
        Cell: ({ cell }: any) => {
          const type = (cell.getValue() || "").toUpperCase();
          return (
            <Typography fontWeight={600} color="#0F172A">
              {type || "—"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Classification Badge",
        size: 180,
        Cell: ({ row }: any) => (
          <Box
            sx={{
              backgroundColor: row.original.color || "#0072BC",
              borderRadius: "4px",
              padding: "4px 10px",
              display: "inline-block",
              minWidth: "60px",
              textAlign: "center",
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            }}
          >
            <Typography sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: 13 }}>
              {row.original.name}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "main_description",
        header: "Main Description",
        size: 380,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        size: 120,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <RowActions
            actions={[
              {
                label: "Edit",
                onClick: () => handleOpenRatingFormDialogue(row.original),
              },
            ]}
          />
        ),
      },
    ],
    [page, pageSize]
  );

  return (
    <DefaultPage
      title="Movie Ratings"
      subTitle="Configure rating classification standards and advisory criteria."
      loading={isLoading}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays official movie ratings. Click <strong>Edit</strong> on any row to configure ratings and descriptions.
        </Alert>
      </Box>

      {isError && (
        <Box mb={2.5}>
          <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Failed to load ratings.
          </Alert>
        </Box>
      )}

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              size="small"
              label="Search Rating / Type / Description"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(totalCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={paginatedData}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: any) => String(row.id)}
        isLoading={isLoading}
        isFetching={isFetching}
        onRowClick={(row: any) => handleOpenRatingFormDialogue(row.original)}
      />

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />

      {/* Modern, Clean Edit Dialog with Strict 6px Border Radius */}
      <Dialog
        open={ratingFormModalState}
        onClose={handleCloseRatingFormDialogue}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18, borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <span>Edit Rating Details</span>
            {selectedItem?.name && (
              <Box
                sx={{
                  backgroundColor: selectedItem.color || "#0072BC",
                  borderRadius: "4px",
                  px: 1,
                  py: 0.25,
                }}
              >
                <Typography sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: 12 }}>
                  {selectedItem.name} ({selectedItem.type?.toUpperCase()})
                </Typography>
              </Box>
            )}
          </Box>
          <Button
            size="small"
            onClick={handleCloseRatingFormDialogue}
            sx={{ minWidth: 32, p: 0.5, color: "#94A3B8" }}
          >
            <Close fontSize="small" />
          </Button>
        </DialogTitle>
        <form noValidate onSubmit={formik.handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <TextField
                size="small"
                fullWidth
                label="Main Description"
                name="main_description"
                multiline
                rows={2}
                value={formik.values.main_description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.main_description && Boolean(formik.errors.main_description)}
                helperText={formik.touched.main_description && (formik.errors.main_description as any)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px" } }}
              />

              <TextField
                size="small"
                fullWidth
                label="General Description"
                name="description"
                multiline
                rows={2}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && (formik.errors.description as any)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px" } }}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Theme"
                  name="theme"
                  value={formik.values.theme}
                  onChange={formik.handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px" } }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Language"
                  name="language"
                  value={formik.values.language}
                  onChange={formik.handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px" } }}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Violence"
                  name="violence"
                  value={formik.values.violence}
                  onChange={formik.handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px" } }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Nudity"
                  name="nudity"
                  value={formik.values.nudity}
                  onChange={formik.handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px" } }}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Sex"
                  name="sex"
                  value={formik.values.sex}
                  onChange={formik.handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px" } }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Horror"
                  name="horror"
                  value={formik.values.horror}
                  onChange={formik.handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px" } }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Drugs"
                  name="drugs"
                  value={formik.values.drugs}
                  onChange={formik.handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px" } }}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 1, borderTop: "1px solid #F1F5F9" }}>
            <Button
              onClick={handleCloseRatingFormDialogue}
              sx={{ borderRadius: "4px", textTransform: "none", color: "#64748B" }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={ratingSubmitLoading}
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                backgroundColor: "#0072BC",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#005a96" },
              }}
            >
              Save Rating
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </DefaultPage>
  );
}
